/**
 * RENDER_FROM_TRUTH — pure helpers for the derived message list.
 *
 * Target model: the rendered list derives strictly from seq-ordered server
 * rows (`messages.seq`, DB insert order — global, never-null, monotonic per
 * task); streaming is a thin ephemeral overlay that never owns identity. The
 * overlay dies ONLY when its server row arrives in the committed list — never
 * on a lifecycle change (killing it on lifecycle was the "half output then
 * everything appeared" bug).
 *
 * Everything here is pure.
 *
 * The identity/order core (orderBySeq, overlayDead) lives in
 * @bash/shared/conversation so the extension side panel renders from the
 * exact same brain; this module re-exports it and keeps the web-render
 * halves (React key bridging, tool carry) that only this app needs.
 */
import { orderBySeq, overlayDead } from "@bash/shared";
import type { NoticeBlock } from "@/types";
import type { ContentBlock, Message, ToolUseBlock } from "./types";
import { findMessageByAnyId } from "./helpers/findMessage";
import { applyCarry, buildToolCarryMap } from "./helpers/toolCarry";

export { orderBySeq, overlayDead };

/**
 * React render key for a message row: the clientId when present — the
 * overlay and the committed row it becomes (settleOverlay bridges the
 * clientId) share one key, so the streaming→committed handoff never
 * remounts the bubble. Rows this client never streamed key by their id.
 */
export function renderKeyOf(m: Pick<Message, "id" | "identity">): string {
  return m.identity?.clientId ?? m.id;
}

/**
 * Settle the overlay against a committed list: if its server row has
 * arrived, the overlay dies — its clientId bridges onto the arrived row
 * (React keys survive the handoff) and its live tool decorations carry
 * over fill-if-missing. If the row hasn't arrived, both pass through
 * untouched: lifecycle alone NEVER kills the overlay (that was the "half
 * output" bug).
 *
 * One exception — the ORPHAN overlay: a reconnect gap can cut a turn
 * before AGENT_END, leaving an overlay with no expectedServerId to match.
 * When the caller reports the turn ended AND the payload introduced an
 * assistant row this client had never seen (the turn's output arrived
 * under an id we never learned), the orphan dies too — otherwise it
 * renders forever as a phantom half-turn below the real committed row.
 * No key bridge on this path (we can't know which row is ours).
 */
export function settleOverlay(
  overlay: Message | null,
  committed: Message[],
  opts?: { turnEndedWithNewAssistantRow?: boolean }
): { overlay: Message | null; committed: Message[] } {
  if (!overlay) return { overlay, committed };
  if (!overlayDead(overlay, committed)) {
    // Orphans only: an overlay holding an expectedServerId (or sdk uuid)
    // knows exactly which row it awaits — an unrelated new assistant row
    // (e.g. a backfilled earlier turn) must not take it down.
    const isOrphan = !overlay.expectedServerId && !overlay.sdkMessageUuid;
    if (isOrphan && opts?.turnEndedWithNewAssistantRow)
      return { overlay: null, committed };
    return { overlay, committed };
  }
  const clientId = overlay.identity?.clientId ?? overlay.id;
  const overlayCarry = buildToolCarryMap([overlay]);
  const expected = overlay.expectedServerId;
  const sdkUuid = overlay.sdkMessageUuid;
  const next = committed.map((row) => {
    const isMatch =
      (expected !== undefined &&
        (row.id === expected ||
          (row.identity?.kind === "persisted" &&
            row.identity.serverId === expected))) ||
      (sdkUuid !== undefined && row.sdkMessageUuid === sdkUuid);
    if (!isMatch) return row;
    const withKey =
      row.identity?.kind === "persisted"
        ? { ...row, identity: { ...row.identity, clientId } }
        : row;
    if (overlayCarry.size === 0) return withKey;
    return {
      ...withKey,
      content: withKey.content.map((block: ContentBlock) => {
        if (block.type !== "tool_use") return block;
        const carry = overlayCarry.get((block as ToolUseBlock).toolUseId);
        return carry ? applyCarry(block, carry) : block;
      }),
    };
  });
  return { overlay: null, committed: next };
}

/**
 * MESSAGE_SYNC adoption — the flag-ON replacement for the local-order-
 * authoritative identity merge. Server rows are TRUTH: their content wins
 * and their `seq` decides order. Local state contributes exactly three
 * things, none of which is order or identity:
 *
 *   1. Tool carry — live results/subagent decorations the DB write hasn't
 *      landed yet (same fill-if-missing semantics as the legacy merge).
 *   2. Render-key stability — an adopted row inherits its local
 *      counterpart's clientId so React keys survive the id flip.
 *   3. Retention — CLIENT-ONLY local rows (optimistic sends, client-only
 *      markers, streamed rows a redeploy lost) are never deleted; they stay
 *      glued after their local predecessor via `orderBySeq`'s seq-less rule.
 *      A PERSISTED local row absent from the (full) payload is DROPPED — the
 *      server once owned it and no longer has it (rewind); retaining it
 *      would visually undo the deletion.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when an unmatched local row survives adoption. Client-only rows
 * (optimistic sends, markers) always survive. A persisted-identity row
 * WITHOUT seq is client-committed awaiting its server echo (a promoted held
 * overlay) — the write may still be in flight, so it survives too. Only
 * seq-bearing persisted rows (adopted from a payload — server-confirmed) and
 * identity-less uuid rows (legacy synced rows) are droppable: absent from a
 * full payload means the server deleted them (rewind).
 */
function isRetainedRow(m: Message): boolean {
  if (m.identity?.kind === "optimistic") return true;
  if (m.identity?.kind === "persisted") return typeof m.seq !== "number";
  return !UUID_RE.test(m.id);
}

/** The refs of the notice blocks a row carries. */
function noticeRefsOf(m: Message): Set<string> {
  const refs = new Set<string>();
  for (const b of m.content)
    if (b.type === "notice") refs.add((b as NoticeBlock).ref);
  return refs;
}

/**
 * The overlay with every notice a committed row already carries taken out. A
 * TASK_NOTICE that lands mid-turn goes INTO the live turn (noticeHandlers),
 * while the server keeps it as its own row; once a sync adopted that row the
 * overlay's copy steps aside, so the line shows once (engines T5 notices).
 * Returns the overlay itself when nothing is shared.
 */
export function withoutAdoptedNotices(
  overlay: Message | null,
  committed: Message[]
): Message | null {
  if (!overlay || !overlay.content.some((b) => b.type === "notice"))
    return overlay;
  const kept = new Set<string>();
  for (const m of committed) for (const r of noticeRefsOf(m)) kept.add(r);
  const content = overlay.content.filter(
    (b) => b.type !== "notice" || !kept.has((b as NoticeBlock).ref)
  );
  return content.length === overlay.content.length
    ? overlay
    : { ...overlay, content };
}

export function adoptServerRows(
  local: Message[],
  serverRows: Message[],
  opts?: { newAssistantArrived?: boolean }
): Message[] {
  const localCarry = buildToolCarryMap(local);
  const withLocalCarry = (msg: Message): Message =>
    localCarry.size === 0
      ? msg
      : {
          ...msg,
          content: msg.content.map((block: ContentBlock) => {
            if (block.type !== "tool_use") return block;
            const carry = localCarry.get((block as ToolUseBlock).toolUseId);
            return carry ? applyCarry(block, carry) : block;
          }),
        };

  // Adopt in PAYLOAD order — the server sorts its payload by the canonical
  // comparator (message-row-order.ts), so payload order IS seq order even for
  // rows that don't carry a seq field. orderBySeq at the end only matters for
  // scrambled seq-bearing payloads; for seq-less rows it's a stable no-op.
  const adopted: Message[] = [];
  const adoptedPosByLocalIndex = new Map<number, number>();
  const matchedLocal = new Set<number>();
  const textOf = (m: Message): string =>
    m.content
      .filter(
        (b): b is Extract<ContentBlock, { type: "text" }> => b.type === "text"
      )
      .map((b) => b.text)
      .join("");
  for (const sm of serverRows) {
    let idx = findMessageByAnyId(local, sm.id);
    if (idx === -1 && sm.role === "user") {
      // Optimistic-by-content fallback, excluding indices already claimed by
      // an earlier server row — two identical-text sends (double-tap, repeated
      // HITL "yes") must adopt to two distinct rows, not fight over the last.
      const text = textOf(sm);
      for (let i = local.length - 1; i >= 0; i--) {
        if (matchedLocal.has(i)) continue;
        const cand = local[i];
        if (cand.role !== "user") continue;
        const kind =
          cand.identity?.kind ??
          (UUID_RE.test(cand.id) ? "persisted" : "optimistic");
        if (kind !== "optimistic") continue;
        if (textOf(cand) === text) {
          idx = i;
          break;
        }
      }
    }
    if (idx === -1 && sm.role === "assistant") {
      // A notice told live (noticeHandlers: an optimistic `msg-notice-<ref>`
      // row) and persisted as its own row is ONE line. Its ref names it in
      // the task, so the persisted row adopts the live one by ref, never
      // leaving the live copy retained beside it (engines T5 notices).
      const refs = noticeRefsOf(sm);
      if (refs.size > 0) {
        idx = local.findIndex(
          (cand, i) =>
            !matchedLocal.has(i) &&
            cand.identity?.kind === "optimistic" &&
            cand.content.length > 0 &&
            cand.content.every(
              (b) => b.type === "notice" && refs.has((b as NoticeBlock).ref)
            )
        );
      }
    }
    const isFirstMatch = idx !== -1 && !matchedLocal.has(idx);
    const counterpart = isFirstMatch ? local[idx] : undefined;
    const clientId = counterpart?.identity?.clientId;
    adopted.push(
      withLocalCarry(
        clientId && sm.identity?.kind === "persisted"
          ? { ...sm, identity: { ...sm.identity, clientId } }
          : sm
      )
    );
    if (isFirstMatch) {
      matchedLocal.add(idx);
      adoptedPosByLocalIndex.set(idx, adopted.length - 1);
    }
  }

  // Retained client-only rows glue after the adopted position of their nearest
  // preceding matched local row (head when none). Persisted rows the server no
  // longer ships are dropped (rewind).
  const glued = new Map<number, Message[]>(); // adopted index (-1 = head) → rows
  let anchor = -1;
  local.forEach((row, i) => {
    const pos = adoptedPosByLocalIndex.get(i);
    if (pos !== undefined) {
      anchor = pos;
      return;
    }
    // A promoted interrupt orphan dies once its DB flush lands — signalled by
    // any never-seen assistant row in the payload (the overlay orphan rule,
    // extended past the overlay slot). Otherwise it would be retained forever
    // alongside its own flushed copy.
    if (row.awaitingEcho && opts?.newAssistantArrived) return;
    if (!isRetainedRow(row)) return;
    const list = glued.get(anchor);
    if (list) list.push(row);
    else glued.set(anchor, [row]);
  });

  const rebuilt: Message[] = [...(glued.get(-1) ?? [])];
  adopted.forEach((row, i) => {
    rebuilt.push(row);
    const list = glued.get(i);
    if (list) rebuilt.push(...list);
  });
  return orderBySeq(rebuilt);
}
